import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ErrorMessage from '../common/ErrorMessage';
import LoadingSpinner from '../common/LoadingSpinner';
import OAuthButton from './OAuthButton';
import { getErrorMessage } from '../../services/errors';

interface RegisterFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ 
  onSuccess, 
  redirectTo = '/dashboard' 
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: '',
    color: '#dc3545'
  });

  const { register, oauthLogin } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Update password strength when password changes
    if (name === 'password') {
      updatePasswordStrength(value);
    }
  };

  const updatePasswordStrength = (password: string) => {
    let score = 0;
    let feedback = '';
    let color = '#dc3545';

    if (password.length === 0) {
      setPasswordStrength({ score: 0, feedback: '', color });
      return;
    }

    // Length check
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;

    // Character variety checks
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    // Set feedback based on score
    if (score < 3) {
      feedback = 'Weak';
      color = '#dc3545';
    } else if (score < 5) {
      feedback = 'Medium';
      color = '#ffc107';
    } else {
      feedback = 'Strong';
      color = '#28a745';
    }

    setPasswordStrength({ score, feedback, color });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate form
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.firstName.trim() || undefined, formData.lastName.trim() || undefined);
      setSuccess('Registration successful! Please check your email to verify your account.');
      setFormData({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '' });
      setAcceptTerms(false);
      
      // Don't navigate immediately - user needs to verify email
    } catch (err: any) {
      const errorMessage = getErrorMessage(err, 'Registration failed. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthRegister = async (provider: string, token: string, extras?: { redirectUri?: string; codeVerifier?: string }) => {
    setError('');
    setLoading(true);

    try {
      await oauthLogin(provider, token, extras);
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(redirectTo);
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || `${provider} registration failed. Please try again.`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    const pwd = formData.password;

    if (pwd.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (!/[A-Z]/.test(pwd)) {
      setError('Password must contain at least one uppercase letter');
      return false;
    }
    if (!/[a-z]/.test(pwd)) {
      setError('Password must contain at least one lowercase letter');
      return false;
    }
    if (!/[0-9]/.test(pwd)) {
      setError('Password must contain at least one number');
      return false;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(pwd)) {
      setError('Password must contain at least one special character');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!acceptTerms) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return false;
    }

    return true;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isPasswordValid = (pwd: string) =>
    pwd.length >= 8 &&
    /[A-Z]/.test(pwd) &&
    /[a-z]/.test(pwd) &&
    /[0-9]/.test(pwd) &&
    /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(pwd);

  const isFormValid =
    formData.email &&
    formData.password &&
    formData.confirmPassword &&
    validateEmail(formData.email) &&
    isPasswordValid(formData.password) &&
    formData.password === formData.confirmPassword &&
    acceptTerms;

  return (
    <div className="auth-card">
      <div className="auth-card-header">
        <img src="/logo.png" alt="KeeDocs" className="auth-logo" />
        <h2>Create your account</h2>
        <p>Start sharing documents securely for free</p>
      </div>

      <div className="auth-card-body">
        {error && (
          <ErrorMessage 
            message={error} 
            onDismiss={() => setError('')} 
          />
        )}

        {success && (
          <div className="alert alert-success">
            <strong>Success!</strong> {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">First Name</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                className="form-control"
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="First name"
                autoComplete="given-name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName" className="form-label">Last Name</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                className="form-control"
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Last name"
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-control ${formData.email && !validateEmail(formData.email) ? 'is-invalid' : ''}`}
              value={formData.email}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Enter your email address"
              autoComplete="email"
            />
            {formData.email && !validateEmail(formData.email) && (
              <div className="invalid-feedback">
                Please enter a valid email address
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password *
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-control"
              value={formData.password}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Create a strong password"
              autoComplete="new-password"
            />
            {formData.password && (
              <div className="password-strength mt-2">
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">Password strength:</small>
                  <small style={{ color: passwordStrength.color, fontWeight: 'bold' }}>
                    {passwordStrength.feedback}
                  </small>
                </div>
                <div className="progress" style={{ height: '4px' }}>
                  <div 
                    className="progress-bar" 
                    style={{ 
                      width: `${(passwordStrength.score / 6) * 100}%`,
                      backgroundColor: passwordStrength.color
                    }}
                  />
                </div>
                <small className="text-muted">
                  Use 8+ characters with uppercase, lowercase, numbers, and symbols
                </small>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password *
            </label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              className={`form-control ${
                formData.confirmPassword && formData.password !== formData.confirmPassword ? 'is-invalid' : ''
              }`}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Confirm your password"
              autoComplete="new-password"
            />
            {formData.confirmPassword && formData.password !== formData.confirmPassword && (
              <div className="invalid-feedback">
                Passwords do not match
              </div>
            )}
          </div>

          <div className="form-group">
            <div className="form-check">
              <input
                type="checkbox"
                id="acceptTerms"
                className="form-check-input"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                disabled={loading}
                required
              />
              <label htmlFor="acceptTerms" className="form-check-label">
                I agree to the{' '}
                <Link to="/terms" target="_blank" style={{ color: '#007bff' }}>
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" style={{ color: '#007bff' }}>
                  Privacy Policy
                </Link>
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={loading || !isFormValid}
            style={{ marginBottom: '15px' }}
          >
            {loading ? <LoadingSpinner size="small" inline /> : 'Create Account'}
          </button>
        </form>

        <div style={{ margin: '20px 0', textAlign: 'center' }}>
          <span className="text-muted">or sign up with</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <OAuthButton provider="gmail" onOAuthLogin={handleOAuthRegister} disabled={loading} mode="register" />
          <OAuthButton provider="linkedin" onOAuthLogin={handleOAuthRegister} disabled={loading} mode="register" />
          <OAuthButton provider="facebook" onOAuthLogin={handleOAuthRegister} disabled={loading} mode="register" />
          <OAuthButton provider="twitter" onOAuthLogin={handleOAuthRegister} disabled={loading} mode="register" />
        </div>

        <div className="text-center mt-4">
          <p className="text-muted">
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#007bff' }}>
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;